import React from 'react';
import { formatIndianCurrency } from '../accounts/utils/accountUtils';
import { getDivisionConfig } from '../../config/divisions';

const DealDetailsModal = ({ isOpen, onClose, deal }) => {
  if (!isOpen || !deal) return null;

  const divisionConfig = getDivisionConfig(deal.Division);

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
  };

  const DealCard = ({ deal }) => {
    if (!deal) return null;

    return (
      <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 h-full">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {deal.AccountName}
                </h3>
                <p className="text-sm text-gray-600">{deal.ServiceType}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-3 mb-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${divisionConfig.badgeColor}`}>
                  {deal.Division}
                </span>
              </div>
              <span className="text-2xl font-bold text-green-600">
                {formatIndianCurrency(deal.DealValue)}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Basic Deal Information */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Expected Closure Date</dt>
              <dd className="mt-1 text-sm text-gray-900 font-medium">{formatDate(deal.ExpectedClosureDate)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Lead Source</dt>
              <dd className="mt-1 text-sm text-gray-900">{deal.LeadSource || 'N/A'}</dd>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Status</dt>
              <dd className="mt-1">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  deal.Status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                  deal.Status === 'PENDING' ? 'bg-orange-100 text-orange-800' :
                  deal.Status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {deal.Status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Stage</dt>
              <dd className="mt-1 text-sm text-gray-900">{deal.Stage?.replace('_', ' ') || 'N/A'}</dd>
            </div>
          </div>

          {/* Sales Team Information */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Salesperson</dt>
              <dd className="mt-1 text-sm text-gray-900 font-medium">{deal.SalespersonName}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Lead Generated By</dt>
              <dd className="mt-1 text-sm text-gray-900">{deal.LeadGeneratorName || 'N/A'}</dd>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Contact Name</dt>
            <dd className="mt-1 text-sm text-gray-900 font-medium">{deal.ContactName}</dd>
          </div>

          {deal.ContactEmail && (
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Contact Email</dt>
              <dd className="mt-1 text-sm text-blue-600">
                <a href={`mailto:${deal.ContactEmail}`} className="hover:underline flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {deal.ContactEmail}
                </a>
              </dd>
            </div>
          )}

          {deal.ContactPhone && (
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Contact Phone</dt>
              <dd className="mt-1 text-sm text-blue-600">
                <a href={`tel:${deal.ContactPhone}`} className="hover:underline flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {deal.ContactPhone}
                </a>
              </dd>
            </div>
          )}

          {/* Account Information */}
          {deal.AccountIndustry && (
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Account Industry</dt>
              <dd className="mt-1 text-sm text-gray-900">{deal.AccountIndustry}</dd>
            </div>
          )}

          {deal.AccountWebsite && (
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Account Website</dt>
              <dd className="mt-1 text-sm text-blue-600">
                <a 
                  href={deal.AccountWebsite.startsWith('http') ? deal.AccountWebsite : `https://${deal.AccountWebsite}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:underline flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  {deal.AccountWebsite}
                </a>
              </dd>
            </div>
          )}

          {deal.AccountTurnover && (
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Account Turnover</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatIndianCurrency(deal.AccountTurnover)}</dd>
            </div>
          )}

          <div className="grid grid-cols-2 gap-6">
            {deal.AccountDivision && (
              <div>
                <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Account Division</dt>
                <dd className="mt-1 text-sm text-gray-900">{deal.AccountDivision}</dd>
              </div>
            )}
            {deal.AccountLocation && (
              <div>
                <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Account Location</dt>
                <dd className="mt-1 text-sm text-gray-900">{deal.AccountLocation}</dd>
              </div>
            )}
          </div>

          {/* Notes */}
          {deal.Notes && (
            <div>
              <dt className="text-sm font-medium text-gray-500 uppercase tracking-wide">Notes</dt>
              <dd className="mt-1 text-sm text-gray-900 bg-white p-3 rounded border">
                {deal.Notes}
              </dd>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <svg className="w-6 h-6 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Deal Details
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Complete information for this deal
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(95vh-140px)]">
          <DealCard deal={deal} />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealDetailsModal;